import * as THREE from 'three';
import { BALL } from '../game/constants';
import { buildFootballGeometry } from '../assets/FootballGeometry';
import type { PhysicsWorld } from '../systems/PhysicsWorld';

export type BallState = 'free' | 'possessed' | 'shot' | 'held';

export class Ball {
  readonly group = new THREE.Group();
  readonly mesh: THREE.Mesh;
  readonly material: THREE.MeshStandardMaterial;
  state: BallState = 'free';
  ownerId: string | null = null;
  lastKickerId: string | null = null;
  pickupLockId: string | null = null;
  pickupLockTimer = 0;
  powerShot = false;
  isExplosive = false;

  private readonly position = new THREE.Vector3();

  constructor(private readonly physics: PhysicsWorld) {
    this.material = new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 0.68,
      metalness: 0.05,
      emissive: new THREE.Color('#001133'),
      emissiveIntensity: 0,
    });
    this.mesh = new THREE.Mesh(buildFootballGeometry(BALL.radius), this.material);
    this.mesh.castShadow = true;
    this.group.add(this.mesh);
  }

  spawn(position: THREE.Vector3): void {
    this.physics.spawnBall(position);
    this.state = 'free';
    this.ownerId = null;
  }

  reset(position: THREE.Vector3): void {
    this.physics.resetBall(position);
    this.state = 'free';
    this.ownerId = null;
    this.lastKickerId = null;
    this.pickupLockId = null;
    this.pickupLockTimer = 0;
    this.powerShot = false;
    this.setExplosive(false);
    this.material.emissiveIntensity = 0;
  }

  setExplosive(explosive: boolean): void {
    this.isExplosive = explosive;
    this.material.emissive.set(explosive ? '#ff3b16' : '#001133');
  }

  getPosition(target: THREE.Vector3): THREE.Vector3 {
    const body = this.physics.ballBody;
    if (!body) return target.set(0, 0, 0);
    const t = body.translation();
    return target.set(t.x, t.y, t.z);
  }

  getVelocity(target: THREE.Vector3): THREE.Vector3 {
    const body = this.physics.ballBody;
    if (!body) return target.set(0, 0, 0);
    const v = body.linvel();
    return target.set(v.x, v.y, v.z);
  }

  speed(): number {
    const body = this.physics.ballBody;
    if (!body) return 0;
    const v = body.linvel();
    return Math.hypot(v.x, v.y, v.z);
  }

  setVelocity(velocity: THREE.Vector3): void {
    this.physics.ballBody?.setLinvel({ x: velocity.x, y: velocity.y, z: velocity.z }, true);
  }

  setPosition(position: THREE.Vector3): void {
    this.physics.ballBody?.setTranslation({ x: position.x, y: position.y, z: position.z }, true);
  }

  applyNetworkState(state: { position: number[]; velocity: number[] }, age = 0): void {
    const lead = Math.min(0.25, Math.max(0, age));
    this.position.fromArray(state.position);
    this.position.x += state.velocity[0] * lead;
    this.position.y = Math.max(BALL.radius, this.position.y + state.velocity[1] * lead);
    this.position.z += state.velocity[2] * lead;
    this.setPosition(this.position);
    this.position.fromArray(state.velocity);
    this.setVelocity(this.position);
  }

  freeze(): void {
    this.physics.ballBody?.setLinvel({ x: 0, y: 0, z: 0 }, true);
    this.physics.ballBody?.setAngvel({ x: 0, y: 0, z: 0 }, true);
  }

  possess(target: THREE.Vector3, dt: number, maxSpeed = 11): void {
    const body = this.physics.ballBody;
    if (!body) return;
    this.getPosition(this.position);
    const dx = target.x - this.position.x;
    const dy = target.y - this.position.y;
    const dz = target.z - this.position.z;
    const scale = 1 / Math.max(dt, 1 / 120);
    let vx = dx * scale * 0.42;
    let vy = dy * scale * 0.42;
    let vz = dz * scale * 0.42;
    const speed = Math.hypot(vx, vy, vz);
    if (speed > maxSpeed) {
      const k = maxSpeed / speed;
      vx *= k;
      vy *= k;
      vz *= k;
    }
    body.setLinvel({ x: vx, y: vy, z: vz }, true);
    body.setAngvel({ x: vz / BALL.radius, y: 0, z: -vx / BALL.radius }, true);
  }

  kick(direction: THREE.Vector3, speed: number, loft: number, kickerId: string): void {
    const body = this.physics.ballBody;
    if (!body) return;
    const dir = direction.clone();
    dir.y = 0;
    if (dir.lengthSq() < 1e-6) dir.set(0, 0, 1);
    dir.normalize();
    const clamped = Math.min(speed, BALL.maxSpeed);
    body.setLinvel(
      {
        x: dir.x * clamped * Math.cos(loft),
        y: clamped * Math.sin(loft) + 0.6,
        z: dir.z * clamped * Math.cos(loft),
      },
      true,
    );
    const spin = clamped * Math.cos(loft) / BALL.radius;
    body.setAngvel({ x: dir.z * spin, y: 0, z: -dir.x * spin }, true);
    this.state = 'shot';
    this.ownerId = null;
    this.lastKickerId = kickerId;
    this.pickupLockId = kickerId;
    this.pickupLockTimer = 0.34;
  }

  applyImpulse(impulse: THREE.Vector3): void {
    this.physics.ballBody?.applyImpulse({ x: impulse.x, y: impulse.y, z: impulse.z }, true);
  }

  update(delta: number): void {
    const body = this.physics.ballBody;
    if (!body) return;
    this.pickupLockTimer = Math.max(0, this.pickupLockTimer - delta);
    if (this.pickupLockTimer <= 0) this.pickupLockId = null;
    const t = body.translation();
    this.mesh.position.set(t.x, t.y, t.z);
    const r = body.rotation();
    this.mesh.quaternion.set(r.x, r.y, r.z, r.w);
    const targetEmissive = this.isExplosive ? 1.1 + (Math.sin(performance.now() * 0.012) + 1) * 0.75 : this.powerShot ? 1.6 : 0;
    this.material.emissiveIntensity += (targetEmissive - this.material.emissiveIntensity) * Math.min(1, delta * 8);
  }
}
